import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { getDataAudit, getDataAuditTables } from '../utils/api';
import {
  FileText, RefreshCw, Download, Search, Filter,
  Plus, Pencil, Trash2, Eye, ChevronDown, ChevronUp, X,
} from 'lucide-react';

const ACTION_CONFIG = {
  create:      { label: 'Created',  color: '#22c55e', bg: 'rgba(34,197,94,0.1)',  icon: Plus    },
  update:      { label: 'Updated',  color: '#3b82f6', bg: 'rgba(59,130,246,0.1)', icon: Pencil  },
  delete:      { label: 'Deleted',  color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  icon: Trash2  },
  bulk_delete: { label: 'Bulk Del', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  icon: Trash2  },
  access:      { label: 'Accessed', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: Eye     },
};

const TABLE_LABELS = {
  cases: 'Cases', case_treatments: 'Treatments', case_consultations: 'Consultations',
  case_medical_bills: 'Medical Bills', case_liens: 'Liens', case_costs: 'Case Costs',
  case_disbursements: 'Disbursements', case_documents: 'Documents', case_notes: 'Notes',
  wc_benefit_payments: 'WC Benefits', wc_medical_charges: 'WC Medical', wc_case_expenses: 'WC Expenses',
  wc_liens: 'WC Liens', wc_billing_flags: 'WC Flags', users: 'Users',
};

function KpiCard({ label, value, color }) {
  return (
    <div style={{
      padding: '14px 16px', background: 'var(--bg-card)', borderRadius: 10,
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

export default function DataAuditPage() {
  const [entries, setEntries]     = useState([]);
  const [stats, setStats]         = useState(null);
  const [count, setCount]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [tables, setTables]       = useState([]);
  const [expanded, setExpanded]   = useState(null);

  // Filters
  const [days, setDays]           = useState(30);
  const [tableName, setTableName] = useState('');
  const [action, setAction]       = useState('');
  const [search, setSearch]       = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage]           = useState(0);
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getDataAudit({
        days, table_name: tableName, action, search,
        limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      });
      setEntries(d.entries || []);
      setStats(d.stats || {});
      setCount(d.count || 0);
    } catch (err) { console.error('Failed to load audit trail:', err); }
    setLoading(false);
  }, [days, tableName, action, search, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getDataAuditTables().then(d => setTables(d.tables || [])).catch(() => {});
  }, []);

  const handleSearch = () => { setSearch(searchInput); setPage(0); };
  const clearFilters = () => {
    setTableName(''); setAction(''); setSearch(''); setSearchInput(''); setPage(0); setDays(30);
  };

  const handleExport = () => {
    if (!entries.length) return;
    const header = ['Timestamp','User','Action','Table','Record ID','Changed Fields','Source'].join(',');
    const rows = entries.map(e =>
      `"${new Date(e.created_at).toLocaleString()}","${e.user_email || 'system'}","${e.action}","${e.table_name}","${e.record_id || ''}","${(e.changed_fields || []).join('; ')}","${e.metadata?.source || 'pg_trigger'}"`
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data-audit-${days}d-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  return (
    <Layout>
      <div className="page-header">
        <div>
          <h1 className="page-title">Data Audit Trail</h1>
          <p className="page-subtitle">Full change history across all sensitive tables -- who changed what, when, and from where</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-sm" onClick={handleExport} disabled={!entries.length}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
            <Download size={13} /> Export CSV
          </button>
          <button className="btn btn-sm" onClick={load} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
            <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI Row */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard label="Total Events" value={(count || 0).toLocaleString()} color="var(--gold)" />
          <KpiCard label="Creates" value={stats.creates || 0} color="#22c55e" />
          <KpiCard label="Updates" value={stats.updates || 0} color="#3b82f6" />
          <KpiCard label="Deletes" value={stats.deletes || 0} color="#ef4444" />
          <KpiCard label="PHI Access" value={stats.accesses || 0} color="#8b5cf6" />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Date pills */}
        {[7, 14, 30, 90].map(d => (
          <button key={d} onClick={() => { setDays(d); setPage(0); }}
            style={{
              padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: days === d ? 700 : 400,
              fontSize: '0.82rem', fontFamily: 'inherit',
              background: days === d ? 'var(--gold)' : 'var(--bg-card)',
              color: days === d ? '#0c1628' : 'var(--text-muted)',
              border: `1px solid ${days === d ? 'var(--gold)' : 'var(--border)'}`,
            }}>{d}d</button>
        ))}

        <div style={{ width: 1, height: 24, background: 'var(--border)', margin: '0 4px' }} />

        {/* Table filter */}
        <select value={tableName} onChange={e => { setTableName(e.target.value); setPage(0); }}
          style={{
            padding: '6px 10px', fontSize: '0.82rem', borderRadius: 8, fontFamily: 'inherit',
            background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)',
          }}>
          <option value="">All Tables</option>
          {tables.map(t => <option key={t} value={t}>{TABLE_LABELS[t] || t}</option>)}
        </select>

        {/* Action filter */}
        <select value={action} onChange={e => { setAction(e.target.value); setPage(0); }}
          style={{
            padding: '6px 10px', fontSize: '0.82rem', borderRadius: 8, fontFamily: 'inherit',
            background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)',
          }}>
          <option value="">All Actions</option>
          <option value="create">Creates</option>
          <option value="update">Updates</option>
          <option value="delete">Deletes</option>
          <option value="access">PHI Access</option>
        </select>

        {/* Search */}
        <div style={{ display: 'flex', gap: 4, flex: '1 1 200px', minWidth: 180 }}>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search by email, table, or record ID..."
            style={{
              flex: 1, padding: '6px 10px', fontSize: '0.82rem', borderRadius: 8, fontFamily: 'inherit',
              background: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)',
            }}
          />
          <button onClick={handleSearch} className="btn btn-sm" style={{ padding: '6px 10px' }}>
            <Search size={13} />
          </button>
        </div>

        {(tableName || action || search) && (
          <button onClick={clearFilters} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
            fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Results */}
      {loading && !entries.length ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} />
          Loading audit trail...
        </div>
      ) : entries.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          No audit entries found for the selected filters.
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div className="card-header">
            <span className="card-title">
              {count.toLocaleString()} event{count !== 1 ? 's' : ''} in the last {days} days
            </span>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.75rem' }}>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', cursor: page === 0 ? 'default' : 'pointer', color: 'var(--text-muted)', fontFamily: 'inherit' }}>
                  Prev
                </button>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                  {page + 1} / {totalPages}
                </span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', cursor: page >= totalPages - 1 ? 'default' : 'pointer', color: 'var(--text-muted)', fontFamily: 'inherit' }}>
                  Next
                </button>
              </div>
            )}
          </div>

          <div>
            {entries.map((e, i) => {
              const acfg = ACTION_CONFIG[e.action] || ACTION_CONFIG.update;
              const Icon = acfg.icon;
              const isExpanded = expanded === e.id;
              const changedCount = (e.changed_fields || []).length;
              const source = e.metadata?.source === 'api' ? 'API' : 'DB Trigger';

              return (
                <div key={e.id}>
                  <div
                    onClick={() => setExpanded(isExpanded ? null : e.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                      borderBottom: '1px solid var(--border)', cursor: 'pointer',
                      background: i % 2 === 0 ? 'transparent' : 'var(--bg-base)',
                    }}
                  >
                    {/* Action icon */}
                    <div style={{
                      width: 28, height: 28, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: acfg.bg, flexShrink: 0,
                    }}>
                      <Icon size={13} style={{ color: acfg.color }} />
                    </div>

                    {/* Main info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {TABLE_LABELS[e.table_name] || e.table_name}
                        </span>
                        <span style={{
                          fontSize: '0.6rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                          background: acfg.bg, color: acfg.color, textTransform: 'uppercase',
                        }}>{acfg.label}</span>
                        {changedCount > 0 && e.action === 'update' && (
                          <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {changedCount} field{changedCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        {e.user_email || 'system'} {e.ip_address ? `from ${e.ip_address}` : ''}
                      </div>
                    </div>

                    {/* Source badge */}
                    <span style={{
                      fontSize: '0.58rem', fontFamily: 'var(--font-mono)', fontWeight: 700,
                      padding: '2px 6px', borderRadius: 6, flexShrink: 0,
                      background: source === 'API' ? 'rgba(59,130,246,0.1)' : 'rgba(168,85,247,0.1)',
                      color: source === 'API' ? 'var(--blue)' : 'var(--purple)',
                    }}>{source}</span>

                    {/* Timestamp */}
                    <div style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', flexShrink: 0, textAlign: 'right', minWidth: 120 }}>
                      {new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
                      {new Date(e.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>

                    {/* Expand arrow */}
                    <div style={{ flexShrink: 0 }}>
                      {isExpanded ? <ChevronUp size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div style={{
                      padding: '12px 16px 12px 56px', borderBottom: '1px solid var(--border)',
                      background: 'var(--bg-base)', fontSize: '0.78rem',
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Record ID</div>
                          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{e.record_id || '--'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>User Agent</div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.user_agent || '--'}</div>
                        </div>
                      </div>

                      {e.changed_fields && e.changed_fields.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>Changed Fields</div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {e.changed_fields.map(f => (
                              <span key={f} style={{
                                padding: '2px 8px', borderRadius: 6, fontSize: '0.68rem', fontFamily: 'var(--font-mono)',
                                background: 'rgba(59,130,246,0.08)', color: 'var(--blue)', fontWeight: 600,
                              }}>{f}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {e.old_values && Object.keys(e.old_values).length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Previous Values</div>
                          <pre style={{
                            background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)',
                            borderRadius: 6, padding: '8px 10px', fontSize: '0.68rem', fontFamily: 'var(--font-mono)',
                            color: 'var(--text-muted)', overflow: 'auto', maxHeight: 150, margin: 0,
                          }}>{JSON.stringify(e.old_values, null, 2)}</pre>
                        </div>
                      )}

                      {e.new_values && Object.keys(e.new_values).length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>New Values</div>
                          <pre style={{
                            background: 'rgba(34,197,94,0.04)', border: '1px solid rgba(34,197,94,0.15)',
                            borderRadius: 6, padding: '8px 10px', fontSize: '0.68rem', fontFamily: 'var(--font-mono)',
                            color: 'var(--text-muted)', overflow: 'auto', maxHeight: 150, margin: 0,
                          }}>{JSON.stringify(e.new_values, null, 2)}</pre>
                        </div>
                      )}

                      {e.metadata && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Metadata</div>
                          <pre style={{
                            background: 'var(--bg-card)', border: '1px solid var(--border)',
                            borderRadius: 6, padding: '8px 10px', fontSize: '0.68rem', fontFamily: 'var(--font-mono)',
                            color: 'var(--text-muted)', overflow: 'auto', maxHeight: 100, margin: 0,
                          }}>{JSON.stringify(e.metadata, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Layout>
  );
}
